import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LocalizationService } from '@abp/ng.core';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { PopoverModule } from 'primeng/popover';
import { BaseListConfig } from './models/base-list-config.model';
import { BaseListColumn } from './models/base-list-column.model';
import { BaseListLazyLoadEvent } from './models/base-list-event.model';
import { DisplayDateTimePipe } from 'src/core/pipes/display-date-time.pipe';
import { CopyOnClickDirective } from 'src/app/shared/directives/copy-on-click.directive';

@Component({
  selector: 'app-base-list',
  standalone: true,
  imports: [CommonModule, TableModule, TooltipModule, PopoverModule, DisplayDateTimePipe, CopyOnClickDirective],
  templateUrl: './base-list.component.html',
  styleUrls: ['./base-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class BaseListComponent<T> {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private _config!: BaseListConfig<T>;
  private _hiddenColumns: string[] = [];
  private _visibleColumns: BaseListColumn<T>[] = [];

  @Input({ required: true })
  set config(value: BaseListConfig<T>) {
    this._config = value;
    this.updateVisibleColumns();
    this.cdr.markForCheck();
  }
  get config(): BaseListConfig<T> {
    return this._config;
  }

  @Input() data: T[] = [];
  @Input() totalRecords = 0;
  @Input() loading = false;
  @Input() rows = 10;
  @Input() paginator = true;
  @Input() first = 0;

  /**
   * Fields of the columns the caller wants hidden. The caller owns (and persists) this set, so the
   * list stays page-agnostic; only columns flagged `hideable` can actually be hidden.
   */
  @Input()
  set hiddenColumns(value: string[]) {
    this._hiddenColumns = value ?? [];
    this.updateVisibleColumns();
    this.cdr.markForCheck();
  }
  get hiddenColumns(): string[] {
    return this._hiddenColumns;
  }

  @Output() lazyLoad = new EventEmitter<BaseListLazyLoadEvent>();
  @Output() rowSelect = new EventEmitter<T>();
  @Output() rowExpand = new EventEmitter<T>();
  @Output() hiddenColumnsChange = new EventEmitter<string[]>();

  @ContentChild('rowExpandTemplate') rowExpandTemplate?: TemplateRef<{ $implicit: T }>;

  /** Passed to p-table [expandedRowKeys] — PrimeNG 20 manages toggle state internally via pRowToggler. */
  expandedRowKeys: { [s: string]: boolean } = {};

  private lastLazyEvent: BaseListLazyLoadEvent | null = null;

  get columns(): BaseListColumn<T>[] {
    return this.config?.columns ?? [];
  }

  /** The columns actually rendered: `columns` minus the hideable ones the caller marked hidden. */
  get visibleColumns(): BaseListColumn<T>[] {
    return this._visibleColumns;
  }

  /** The columns offered by the picker. */
  get hideableColumns(): BaseListColumn<T>[] {
    return this.columns.filter(column => column.hideable === true);
  }

  get showColumnPicker(): boolean {
    return this.config?.showColumnPicker === true && this.hideableColumns.length > 0;
  }

  /** Column count of a full-width cell (empty message, expanded row), expand toggle included. */
  get bodyColspan(): number {
    return this._visibleColumns.length + (this.isExpandable ? 1 : 0);
  }

  get rowsPerPageOptions(): number[] {
    return this.config?.rowsPerPageOptions ?? [10, 25, 50];
  }

  get showGridlines(): boolean {
    return this.config?.showGridlines ?? true;
  }

  get stripedRows(): boolean {
    return this.config?.stripedRows ?? true;
  }

  get rowHover(): boolean {
    return this.config?.rowHover ?? true;
  }

  get emptyMessage(): string {
    return this.config?.emptyMessage ?? this.localization.instant('::NoRecordsFound');
  }

  get isExpandable(): boolean {
    return !!(this.config?.expandable && this.rowExpandTemplate);
  }

  get defaultSortField(): string | undefined {
    return this.config?.defaultSortField;
  }

  get defaultSortOrder(): number {
    return this.config?.defaultSortOrder ?? 1;
  }

  get refreshLabel(): string {
    return this.localize('AbpUi::Refresh', 'Refresh');
  }

  get columnsLabel(): string {
    return this.localize('::Columns', 'Columns');
  }

  get columnPickerLabel(): string {
    return this.localize('::ShowHideColumns', 'Show/hide columns');
  }

  get dataKey(): string {
    return (this.config?.trackByField as string) || 'id';
  }

  /** Placeholder rows painted by the loading skeleton. */
  readonly skeletonRows = [1, 2, 3, 4, 5];

  trackByRow = (_index: number, item: T): unknown => {
    if (this.config?.trackByField && item) {
      const field = this.config.trackByField as keyof T;
      return item[field];
    }
    return _index;
  };

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    const rows = event.rows ?? this.rows;
    const sortField = this.resolveSortField(event.sortField);
    const sortOrder = this.resolveSortOrder(event.sortOrder);

    const lazyEvent: BaseListLazyLoadEvent = {
      skipCount: this.first,
      maxResultCount: rows,
      sortField: sortField ?? undefined,
      sortOrder,
    };

    this.lastLazyEvent = lazyEvent;
    this.lazyLoad.emit(lazyEvent);
  }

  refresh(): void {
    if (this.lastLazyEvent) {
      this.lazyLoad.emit({ ...this.lastLazyEvent });
    }
  }

  onRowClick(row: T): void {
    this.rowSelect.emit(row);
  }

  onRowExpandEvent(event: { data: T }): void {
    this.rowExpand.emit(event.data);
  }

  collapseAll(): void {
    this.expandedRowKeys = {};
    this.cdr.markForCheck();
  }

  isColumnVisible(column: BaseListColumn<T>): boolean {
    return this._visibleColumns.includes(column);
  }

  /** True while `column` is the only one left on screen: hiding it would empty the table. */
  isLastVisibleColumn(column: BaseListColumn<T>): boolean {
    return this._visibleColumns.length === 1 && this._visibleColumns[0] === column;
  }

  toggleColumn(column: BaseListColumn<T>): void {
    if (column.hideable !== true) {
      return;
    }

    const wasVisible = this.isColumnVisible(column);
    if (wasVisible && this.isLastVisibleColumn(column)) {
      return;
    }

    // Deduplicated: the stored set can name a column twice (an older payload, an edited preference),
    // and a second entry would make the next toggle-on leave the column hidden.
    const next = wasVisible
      ? Array.from(new Set([...this._hiddenColumns, column.field]))
      : this._hiddenColumns.filter(field => field !== column.field);

    this.hiddenColumns = next;
    this.hiddenColumnsChange.emit(next);
  }

  getCellValue(row: T, column: BaseListColumn<T>): unknown {
    return row[column.field];
  }

  /** The default text cell's value as it is displayed, for copying; `null` when there is nothing to show. */
  getCellText(row: T, column: BaseListColumn<T>): string | null {
    const value = this.getCellValue(row, column);
    return value === null || value === undefined ? null : String(value);
  }

  getRowClass(row: T): string {
    return this.config?.rowClassFn?.(row) ?? '';
  }

  private localize(key: string, fallback: string): string {
    const value = this.localization.instant(key);
    return value !== key ? value : fallback;
  }

  private updateVisibleColumns(): void {
    const hidden = new Set(this._hiddenColumns);
    const visible = this.columns.filter(
      column => column.hideable !== true || !hidden.has(column.field),
    );

    if (visible.length > 0) {
      this._visibleColumns = visible;
      return;
    }

    // A hidden set that would empty the table is not a state to render, it is a set to discard: kept
    // around, it would leave every checkbox ticked while nothing the user clicks appears to work.
    this._hiddenColumns = [];
    this._visibleColumns = this.columns;
  }

  private resolveSortField(sortField: TableLazyLoadEvent['sortField']): string | undefined {
    const normalized = Array.isArray(sortField) ? sortField[0] : sortField;
    return typeof normalized === 'string' && normalized.length > 0 ? normalized : undefined;
  }

  private resolveSortOrder(sortOrder: TableLazyLoadEvent['sortOrder']): 'asc' | 'desc' | undefined {
    if (sortOrder === 1) return 'asc';
    if (sortOrder === -1) return 'desc';
    return undefined;
  }
}
